import React, { useState, useEffect } from "react"
import { Menu, MenuButton, MenuItem, MenuItems, Transition, Switch } from "@headlessui/react"
import { IoChevronDownOutline } from "react-icons/io5"
import "react-datepicker/dist/react-datepicker.css"
import TransitionAccept from "../DailyCalendarFiles/CustomCalendar/TransitionAccept"
import useFetchCenters from "../../hooks/useFetchCenters"
import { useDispatch } from "react-redux"
import { setCenter } from "../../features/settings/settingsSlice"
import { useSelector } from "react-redux"
import { setIsEditing } from "../../features/schedule/scheduleSlice"
import { setSandboxMode } from "../../features/schedule/sandboxSlice"

const NavButtons = ({ className, hideToggle }) => {
	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)
	const centers = useFetchCenters()
	const center = useSelector(state => state.settings.center)
	const dispatch = useDispatch()
	const [selectedLocation, setSelectedLocation] = useState(center?.name)
	const [isPopupOpen, setIsPopupOpen] = useState(false)

	const handleLocationSelect = option => {
		dispatch(setCenter(option))
		setSelectedLocation(option.name)
	}

	useEffect(() => {
		if (selectedLocation === null || selectedLocation === undefined) {
			setSelectedLocation(center?.name)
		}
		// React setState setters are stable — omitting deps that are setter-only or initial-load reads.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [center])

	const toggleMode = () => {
		if (sandbox) {
			// If switching from Practice Mode to Live Mode, show the confirmation popup
			dispatch(setIsEditing(false))
			setIsPopupOpen(true)
		} else {
			// If switching back to Practice Mode, no confirmation needed
			dispatch(setIsEditing(false))
			dispatch(setSandboxMode(true))
		}
	}

	const handleAccept = () => {
		// User confirmed switching to Live Mode
		dispatch(setSandboxMode(false))
		setIsPopupOpen(false)
	}

	const handleDecline = () => {
		// User declined switching to Live Mode
		setIsPopupOpen(false)
	}

	if (!centers || centers.length === 0 || !center) {
		return null
	}

	return (
		<div className={`flex justify-between items-center w-full ${className}`}>
			<div className="flex items-center space-x-4">
				<Menu as="div" className="relative inline-block text-left">
					<div>
						<MenuButton className="inline-flex items-center justify-center w-full rounded-md bg-gray-200 text-gray-800 px-4 py-2 shadow-sm hover:bg-gray-300">
							{selectedLocation}
							<IoChevronDownOutline className="w-5 h-5 text-gray-400 ml-2" />
						</MenuButton>
					</div>
					<Transition
						enter="transition duration-100 ease-out"
						enterFrom="transform scale-95 opacity-0"
						enterTo="transform scale-100 opacity-100"
						leave="transition duration-75 ease-in"
						leaveFrom="transform scale-100 opacity-100"
						leaveTo="transform scale-95 opacity-0"
						as="div"
					>
						<MenuItems className="absolute left-0 z-10 mt-2 w-56 origin-top-left rounded-md bg-white shadow-lg focus:outline-none">
							<div className="py-1">
								{centers.map(location => (
									<MenuItem key={location._id || location.id}>
										{({ active }) => (
											<button
												onClick={() => handleLocationSelect(location)}
												className={`${
													active
														? "bg-gray-100 text-gray-900"
														: "text-gray-700"
												} block px-4 py-2 w-full text-left`}
											>
												{location.name}
											</button>
										)}
									</MenuItem>
								))}
							</div>
						</MenuItems>
					</Transition>
				</Menu>
			</div>

			{!isTomorrowMode && !hideToggle && (
				<div className="flex items-center space-x-4">
					{/* Practice Mode Label */}
					<span
						className={`text-sm font-medium ${
							!sandbox ? "text-gray-500" : "text-indigo-600"
						}`}
					>
						Practice Mode
					</span>

					<Switch
						checked={!sandbox}
						onChange={toggleMode}
						className={`group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${
							!sandbox ? "bg-indigo-600" : "bg-gray-200 opacity-50 cursor-not-allowed"
						}`}
						disabled={isTomorrowMode}
					>
						<span className="sr-only">Use setting</span>
						<span
							className={`pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
								!sandbox ? "translate-x-5" : "translate-x-0"
							}`}
						>
							<span
								aria-hidden="true"
								className={`absolute inset-0 flex h-full w-full items-center justify-center transition-opacity duration-200 ease-in ${
									!sandbox ? "opacity-0 duration-100 ease-out" : "opacity-100"
								}`}
							>
								<svg
									fill="none"
									viewBox="0 0 12 12"
									className="h-3 w-3 text-gray-400"
								>
									<path
										d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
										stroke="currentColor"
										strokeWidth={2}
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</span>
							<span
								aria-hidden="true"
								className={`absolute inset-0 flex h-full w-full items-center justify-center opacity-0 transition-opacity duration-100 ease-out ${
									!sandbox ? "opacity-100 duration-200 ease-in" : "opacity-0"
								}`}
							>
								<svg
									fill="currentColor"
									viewBox="0 0 12 12"
									className="h-3 w-3 text-indigo-600"
								>
									<path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
								</svg>
							</span>
						</span>
					</Switch>

					{/* Live Mode Label */}
					<span
						className={`text-sm font-medium ${
							!sandbox ? "text-indigo-600" : "text-gray-500"
						}`}
					>
						Live Mode
					</span>
				</div>
			)}

			{/* Confirmation Popup */}
			<TransitionAccept
				title="Switch to Live Mode"
				message="You are about to switch to Live Mode. Changes made in Live Mode will affect patient data and have lasting effects. Are you sure you want to proceed?"
				isOpen={isPopupOpen}
				onAccept={handleAccept}
				onDecline={handleDecline}
			/>
		</div>
	)
}

export default NavButtons
